import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { LayoutData, LyricsIndexItem, PageContent, Tag } from '@/types/content'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/site/page-hero'
import { LyricsLibrary } from '@/components/music/lyrics-library'
import { getLayout, getPage } from '@/lib/server/content/site'
import { getGenres, getLyricsIndex, getMoodTags } from '@/lib/server/content/songs'
import { queryParam, setPageCache } from '@/lib/server/page'

interface LyricsPageProps {
    layout: LayoutData
    page: PageContent | null
    songs: Array<LyricsIndexItem>
    genres: Array<Tag>
    moods: Array<Tag>
    query: string
}

const LyricsIndexPage: FC<LyricsPageProps> = ({ layout, page, songs, genres, moods, query }): ReactNode => {
    return (
        <Layout layout={layout}>
            <Seo
                title={page?.seo_title ?? 'Lyrics'}
                description={page?.seo_description}
                ogImage={page?.og_image ?? layout.settings.og_image}
                ogImageAlt={page?.og_image_alt}
                origin={layout.origin}
                path='/lyrics'
            />

            <PageHero page={page} fallback={{ eyebrow: 'The words', heading: 'Lyrics' }} phase={0.18} image='/images/book-candle.jpg' imageAlt='An open book with a dried rose resting on its pages' />

            <section id='lyrics-library' aria-label='Lyrics library' className='shell pb-28'>
                <LyricsLibrary songs={songs} genres={genres} moods={moods} initialQuery={query} />
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<LyricsPageProps> = async ctx => {
    const [layout, page, songs, genres, moods] = await Promise.all([
        getLayout(ctx.req),
        getPage('lyrics'),
        getLyricsIndex(),
        getGenres(),
        getMoodTags()
    ])

    setPageCache(ctx)

    return { props: { layout, page, songs, genres, moods, query: queryParam(ctx.query.q) } }
}

export default LyricsIndexPage
